import { useRef, useState } from 'react';
import {
    AllowedWebsiteTemplateTransferSchema,
    parseJsonWithNativeSafeNumbers,
    serializeAllowedWebsiteTemplateTransferV1,
    stringifyLosslessJson,
    type AllowedWebsiteTemplateTransfer,
} from '@pillar-portal/api-types';
import { Button } from '@/components/ui/button';

export function downloadAllowedWebsiteTemplateTransfer(
    transfer: AllowedWebsiteTemplateTransfer
): void {
    const payload = stringifyLosslessJson(
        serializeAllowedWebsiteTemplateTransferV1(transfer),
        null,
        2
    );
    const objectUrl = URL.createObjectURL(new Blob([payload], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `${transfer.name
        .replace(/[^a-z0-9]+/giu, '-')
        .replace(/^-|-$/gu, '')
        .toLowerCase()}-allowed-website-template.json`;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
}

export function AllowedWebsiteTemplateImportButton(props: {
    readonly disabled?: boolean;
    readonly onImport: (transfer: AllowedWebsiteTemplateTransfer) => void;
    readonly onError: (message: string) => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isReading, setIsReading] = useState(false);

    async function handleFile(file: File | undefined): Promise<void> {
        if (file === undefined) {
            return;
        }
        setIsReading(true);
        try {
            const parsed: unknown = parseJsonWithNativeSafeNumbers(await file.text());
            props.onImport(AllowedWebsiteTemplateTransferSchema.parse(parsed));
        } finally {
            setIsReading(false);
            if (inputRef.current !== null) {
                inputRef.current.value = '';
            }
        }
    }

    return (
        <>
            <input
                ref={inputRef}
                className="hidden"
                type="file"
                accept="application/json,.json"
                onChange={(event) => {
                    handleFile(event.currentTarget.files?.[0]).catch(() => {
                        props.onError(
                            'The selected file is not a supported allowed website template export.'
                        );
                    });
                }}
            />
            <Button
                variant="outline"
                disabled={props.disabled === true || isReading}
                onClick={() => inputRef.current?.click()}
            >
                {isReading ? 'Reading...' : 'Import'}
            </Button>
        </>
    );
}
